import * as Sentry from "@sentry/vue";

// TODO: use correct environment variable
const IS_AI = true //import.meta.env.VITE_ENVIRONMENT_NAME == 'ai'
const PRESERVED_KEYS = ['id'];
const PRESERVED_KEY_SUFFIXES = ['_id'];

const isPreservedKey = (key) => { 
  return PRESERVED_KEYS.includes(key) || PRESERVED_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
}

/**
 * Masks query params in a URL string to avoid leaking PHI/PII.
 * This can happen on listing pages, where selected table filters update URL
 * and filters can be patient name, mrn, etc.
 * 
 * @param {string} urlString - The URL string to mask
 * @returns {string} The masked URL string
 */
const maskUrlQueryParams = (urlString) => {
  try {
    if (!urlString || typeof urlString !== 'string') {
      return urlString;
    }

    if (!urlString.startsWith('http')) {
      urlString = import.meta.env.VITE_BASE_URL + urlString
    }
    const url = new URL(urlString);

    if (!url.search) {
      return urlString;
    }
    
    const params = new URLSearchParams(url.search);
    for (const [key] of params.entries()) {
      if (!isPreservedKey(key)) {
        params.set(key, '***');
      }
    }
    url.search = params.toString();
    return url.toString();
  } catch {
    return urlString;
  }
};

/**
 * Recursively masks sensitive data in an object to avoid leaking PHI/PII.
 * This function traverses through object properties and arrays, masking all values
 * except those with preserved keys and preserved key suffixes.
 * 
 * @param {Object|Array} data - The data structure to mask. Can be a plain object or array
 * @returns {Object|Array} A new object/array with sensitive data masked with '***'
 * 
 * @example 
 * // Returns { id: 123, name: '***', monitor_id: 123, details: { age: '***' } }
 * maskSensitiveData({ id: 123, name: 'John', monitor_id: 123, details: { age: 30 } })
 * 
 * @example 
 * // Returns [{ id: 1, name: '***' }, { id: 2, name: '***' }]
 * maskSensitiveData([{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }])
 */
const maskSensitiveData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const maskedData = Array.isArray(data) ? [] : {};
  
  for (const [key, value] of Object.entries(data)) {
    if (isPreservedKey(key)) {
      maskedData[key] = value;
    } else if (typeof value === 'object' && value !== null) {
      maskedData[key] = maskSensitiveData(value);
    } else {
      maskedData[key] = '***';
    }
  }
  
  return maskedData;
};

// processing navigation related events such as push, page load, reload, etc
const processNavigationEvent = (event) => {
  if (event?.data?.payload?.data) {
    const { from, to, previous } = event.data.payload.data;
    if (from) { 
      event.data.payload.data.from = maskUrlQueryParams(from);
    }
    if (to) {
      event.data.payload.data.to = maskUrlQueryParams(to);
    }
    if (previous) {
      event.data.payload.data.previous = maskUrlQueryParams(previous);
    }
  }

  if (event?.data?.payload?.description) {
    event.data.payload.description = maskUrlQueryParams(event.data.payload.description);
  }
  
  return event;
};

// processing xhr related events such as api calls
const processXhrEvent = (event) => {
  if (event?.data?.payload?.data?.response?.body) {
    event.data.payload.data.response.body = maskSensitiveData(event.data.payload.data.response.body);
    event.data.payload.data.request.body = maskSensitiveData(event.data.payload.data.request.body);
    
    if (event.data.payload.description) {
      event.data.payload.description = maskUrlQueryParams(event.data.payload.description);
    }
  }
  return event;
};

// breadcrumbs include navigation, xhr, console logs, user clicks and many others
// we're looking only for xhr and navigation events
const processBreadcrumb = (breadcrumb) => {
  if (breadcrumb.category === 'xhr' && breadcrumb.data?.url) {
    breadcrumb.data.url = maskUrlQueryParams(breadcrumb.data.url);
  } else if (breadcrumb.category === 'navigation') {
    if (breadcrumb.data?.from) {
      breadcrumb.data.from = maskUrlQueryParams(breadcrumb.data.from);
    }
    if (breadcrumb.data?.to) {
      breadcrumb.data.to = maskUrlQueryParams(breadcrumb.data.to);
    }
  }
  return breadcrumb;
};

export const initSentry = (app, router) => {
  const apiUrl = import.meta.env.VITE_API_BASE_URL.replace('https://', '')

  const replayIntegration = Sentry.replayIntegration({
    networkDetailAllowUrls: [apiUrl],
    maskAllText: IS_AI,
    maskAllInputs: IS_AI,
    unmask: ['.sidebar-container'],
    beforeAddRecordingEvent: (event) => {
      if (!IS_AI) return event;

      if (event?.data?.payload?.category === 'navigation' || 
          event?.data?.payload?.op?.startsWith('navigation')) {
        event = processNavigationEvent(event);
      }

      if (event?.data?.payload?.op === 'resource.xhr') {
        event = processXhrEvent(event);
      }

      return event;
    }
  });

  const sentryConfig = {
    app,
    dsn: 'https://d7566269227e45c6aff0163de398e0fc@o1089958.ingest.sentry.io/6107167',
    environment: import.meta.env.VITE_ENVIRONMENT_NAME,
    integrations: [
      Sentry.browserTracingIntegration({ 
        router,
        beforeStartSpan(span) {
          if (!IS_AI) return span;
          
          if (span?.op === 'navigation' && span.attributes) {
            span.attributes = maskSensitiveData(span.attributes);
            span.attributes['sentry.source'] = 'custom';
            span.attributes['sentry.origin'] = 'scrubbing';
          }
          return span;
        }
      }),
      replayIntegration,
      Sentry.feedbackIntegration({ autoInject: false })
    ],
    tracePropagationTargets: ['localhost', apiUrl, /^\//],
    tracesSampleRate: 0.1,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    
    beforeBreadcrumb(breadcrumb) {
      if (!IS_AI) return breadcrumb;

      return processBreadcrumb(breadcrumb);
    },
    
    beforeSendTransaction(transaction) {
      if (!IS_AI) return transaction;

      /**
       * This is Sentry's private API.
       * initialUrl and urls are used to track the user's session.
       * Sentry adds user visited pages in urls array.
       * 
       * Data from these keys supposedly used in Sentry's UI.
       * 
       * @see https://docs.sentry.io/platforms/javascript/configuration/integrations/replay/#replayintegration
       */
      if (replayIntegration._replay) {
        replayIntegration._replay._context.initialUrl = maskUrlQueryParams(replayIntegration._replay._context.initialUrl);
        
        const replayUrls = replayIntegration._replay._context.urls;
        if (replayUrls && Array.isArray(replayUrls)) {
          replayIntegration._replay._context.urls = replayUrls.map(maskUrlQueryParams);
        }
      }

      // Process transaction request
      if (transaction?.request?.url) {
        transaction.request.url = maskUrlQueryParams(transaction.request.url);
      }
      transaction.spans = transaction.spans.map(span => {
        if (span.op.startsWith('browser')) {
          span.description = maskUrlQueryParams(span.description)
        }

        if (span.op === 'http.client') {
          const [method, url] = span.description.split(' ');
          if (url) {
            span.description = `${method} ${maskUrlQueryParams(url)}`;

            if (span.data?.['http.url']) {
              span.data['http.url'] = maskUrlQueryParams(span.data['http.url']);
            }

            if (span.data?.url) {
              span.data.url = maskUrlQueryParams(span.data.url);
            }
          }
        }
        return span;
      })

      // Process trace context
      if (transaction?.contexts?.trace?.op === 'pageload' && transaction.contexts.trace.data) {
        const maskedData = { ...transaction.contexts.trace.data };
        for (const [key] of Object.entries(maskedData)) {
          if (key.startsWith('query.') && !isPreservedKey(key.replace('query.', ''))) {
            maskedData[key] = '***';
          }
        }
        transaction.contexts.trace.data = maskedData;
      }

      return transaction;
    },

    beforeSend(event) {
      if (!IS_AI) return event;
      
      if (event?.request?.url) {
        event.request.url = maskUrlQueryParams(event.request.url);
      }
      if (event?.request?.headers?.Referer) {
        event.request.headers.Referer = maskUrlQueryParams(event.request.headers.Referer);
      }
      
      if (event?.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(processBreadcrumb);
      }
      
      return event;
    }
  };

  Sentry.init(sentryConfig);
}; 